import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMerchants } from '../../merchants/hooks/useMerchants';
import { StageChip } from './StageChip';
import { useStorefrontReadiness } from '../hooks/useStorefrontReadiness';
import {
  getReadiness,
  READINESS_LABELS,
  READINESS_ORDER,
  type ReadinessState,
} from '../readiness';
import type { StorefrontStatus } from '../types';

/** Verdict pill — mirrors StageChip's palette so the board reads as one system. */
const VERDICT_TONE: Record<ReadinessState, string> = {
  live: 'bg-green-100 text-green-800',
  ready: 'bg-green-100 text-green-800',
  'in-progress': 'bg-amber-100 text-amber-700',
  'not-started': 'bg-muted text-muted-foreground',
  unknown: 'bg-muted text-muted-foreground',
};

function VerdictBadge({ state }: { state: ReadinessState }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${VERDICT_TONE[state]}`}>
      {state === 'live' ? '● ' : ''}
      {READINESS_LABELS[state]}
    </span>
  );
}

interface Row {
  id: string;
  name: string;
  slug: string;
  solutionId: string;
  status: StorefrontStatus | undefined;
  isLoading: boolean;
  isError: boolean;
  state: ReadinessState;
}

export default function StorefrontReadinessBoard() {
  const { data: merchants = [], isLoading: merchantsLoading } = useMerchants();
  const slugs = useMemo(() => merchants.map((m) => m.slug).filter(Boolean), [merchants]);
  const { results, isLoading: statusesLoading, isFetching } = useStorefrontReadiness(slugs);

  const statusBySlug = useMemo(() => {
    const map = new Map<string, (typeof results)[number]>();
    for (const r of results) map.set(r.slug, r);
    return map;
  }, [results]);

  const rows = useMemo<Row[]>(() => {
    const built = merchants.map((m): Row => {
      const r = statusBySlug.get(m.slug);
      return {
        id: m.id,
        name: m.name,
        slug: m.slug,
        solutionId: m.solutionId,
        status: r?.status,
        isLoading: r?.isLoading ?? true,
        isError: r?.isError ?? false,
        state: getReadiness(r?.status).state,
      };
    });
    return built.sort((a, b) => {
      const d = READINESS_ORDER.indexOf(a.state) - READINESS_ORDER.indexOf(b.state);
      return d !== 0 ? d : a.name.localeCompare(b.name);
    });
  }, [merchants, statusBySlug]);

  const counts = useMemo(() => {
    const c: Record<ReadinessState, number> = { ready: 0, 'in-progress': 0, 'not-started': 0, live: 0, unknown: 0 };
    for (const row of rows) c[row.state] += 1;
    return c;
  }, [rows]);

  const loading = merchantsLoading || (statusesLoading && rows.length === 0);

  const SUMMARY: Array<{ state: ReadinessState; tone: string }> = [
    { state: 'ready', tone: 'text-green-700' },
    { state: 'in-progress', tone: 'text-amber-700' },
    { state: 'not-started', tone: 'text-muted-foreground' },
    { state: 'live', tone: 'text-green-700' },
  ];

  return (
    <div className="space-y-6 py-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Storefront Readiness</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Go-live readiness for every storefront tenant — tenant · infra · deploy at a glance.
            A tenant is <span className="font-medium text-foreground">Ready to go live</span> once it is
            created, keyed, and its repo + site are provisioned.
          </p>
        </div>
        {isFetching && <span className="text-xs text-muted-foreground mt-1 shrink-0">Refreshing…</span>}
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {SUMMARY.map(({ state, tone }) => (
          <div key={state} className="bg-card rounded-lg border border-border p-4">
            <div className={`text-2xl font-bold ${tone}`}>{counts[state]}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{READINESS_LABELS[state]}</div>
          </div>
        ))}
      </div>

      {/* Rows */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">No storefront tenants yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <Link
              key={row.id}
              to={`/merchants/${row.id}`}
              className="block bg-card rounded-lg border border-border p-4 hover:border-foreground/30 transition-colors"
            >
              <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                {/* Identity */}
                <div className="min-w-[180px] flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground truncate">{row.name}</span>
                    <VerdictBadge state={row.state} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    <span className="font-mono">{row.slug}</span>
                    {row.solutionId && <span> · {row.solutionId}</span>}
                  </div>
                </div>

                {/* Legs */}
                {row.isError ? (
                  <span className="text-xs text-red-500">Status unavailable</span>
                ) : row.status ? (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <LegGroup label="Tenant">
                      <StageChip label="Created" tone={row.status.tenant.created ? 'done' : 'idle'} />
                      <StageChip
                        label="Onboarded"
                        tone={row.status.tenant.onboarded ? 'done' : row.status.tenant.created ? 'pending' : 'idle'}
                      />
                      <StageChip
                        label="Key"
                        tone={row.status.tenant.keyIssued ? 'done' : row.status.tenant.onboarded ? 'pending' : 'idle'}
                      />
                    </LegGroup>
                    <LegGroup label="Infra">
                      <StageChip label="Repo" tone={row.status.infra.repoProvisioned ? 'done' : 'idle'} />
                      <StageChip label="Site" tone={row.status.infra.siteProvisioned ? 'done' : 'idle'} />
                    </LegGroup>
                    <LegGroup label="Deploy">
                      <StageChip label="Live" tone={row.status.deploy.live ? 'done' : 'idle'} />
                    </LegGroup>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">Loading…</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function LegGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}
